import { isNativeApp, triggerHaptic, type HapticStyle } from './nativeBridge';

/**
 * Vocabulario semántico de hápticos. En vez de exponer estilos crudos del Taptic
 * Engine, cada intención describe QUÉ pasó (una selección, un éxito, un error) y
 * el motor decide la textura — incluyendo secuencias multi-pulso que varían
 * intensidad y ritmo para que se sienta físico, no como un buzz plano.
 */
export type HapticIntent =
	| 'selection' // cambiar de tab / elegir una opción (tick seco)
	| 'tap' // toque de CTA primario
	| 'light'
	| 'medium'
	| 'heavy'
	| 'toggleOn' // encender un switch (click firme)
	| 'toggleOff' // apagar un switch (release suave)
	| 'success' // completar algo (swell suave → notificación de éxito)
	| 'celebrate' // momento estrella: compra confirmada (florecita ascendente)
	| 'warning' // confirmación destructiva / precaución
	| 'error'; // pago rechazado, validación fallida (doble golpe)

type HapticStep = { style: HapticStyle; delay?: number };

/**
 * Cada intención es una SECUENCIA reproducida en el Taptic Engine nativo. El
 * `delay` es el tiempo (ms) que espera ESE paso respecto al anterior, así que un
 * patrón multi-paso se siente como un gesto con forma, no como vibraciones
 * sueltas:
 *  - `error` golpea fuerte y rebota en la notificación de error → "no".
 *  - `success` arranca con un toque suave y florece en la notificación → "listo".
 *  - `celebrate` sube medium → light → success: una pequeña celebración para la
 *    compra, el único momento que merece tanto.
 *  - `toggleOn`/`toggleOff` usan texturas opuestas (rigid vs soft) para que
 *    encender se sienta distinto a apagar.
 */
const HAPTIC_PATTERNS: Record<HapticIntent, HapticStep[]> = {
	selection: [{ style: 'selection' }],
	tap: [{ style: 'light' }],
	light: [{ style: 'light' }],
	medium: [{ style: 'medium' }],
	heavy: [{ style: 'heavy' }],
	toggleOn: [{ style: 'rigid' }],
	toggleOff: [{ style: 'soft' }],
	success: [{ style: 'light' }, { style: 'success', delay: 110 }],
	celebrate: [
		{ style: 'medium' },
		{ style: 'light', delay: 90 },
		{ style: 'success', delay: 120 },
	],
	warning: [{ style: 'rigid' }, { style: 'warning', delay: 120 }],
	error: [{ style: 'heavy' }, { style: 'error', delay: 130 }],
};

// Techo de seguridad: ningún patrón puede agendar pulsos más allá de esto.
const MAX_PATTERN_MS = 600;
// Anti rebote: traga disparos IDÉNTICOS muy seguidos (doble invoke de StrictMode,
// bubbling, un Button + su sheet disparando juntos). Intenciones distintas pasan.
const DEDUP_MS = 60;

let lastIntent: HapticIntent | '' = '';
let lastFiredAt = 0;

/**
 * Reproduce un patrón háptico premium. No-op limpio fuera de la app nativa
 * (navegador/PWA) y a prueba de fallos — delega en el bridge blindado, que nunca
 * lanza. Seguro de llamar desde cualquier handler sin try/catch.
 */
export const playHaptic = (intent: HapticIntent = 'tap'): void => {
	if (!isNativeApp()) return;

	const now = Date.now();
	if (intent === lastIntent && now - lastFiredAt < DEDUP_MS) return;
	lastIntent = intent;
	lastFiredAt = now;

	const steps = HAPTIC_PATTERNS[intent] ?? HAPTIC_PATTERNS.tap;
	let elapsed = 0;
	for (const step of steps) {
		elapsed += step.delay ?? 0;
		if (elapsed <= 0) {
			triggerHaptic(step.style);
		} else if (elapsed <= MAX_PATTERN_MS) {
			setTimeout(() => triggerHaptic(step.style), elapsed);
		}
	}
};
